import type { HeroProps } from '@/components/home/Hero';
import type {
  CustomersReviewsProps,
  GenericProductsProps,
  HowItWorksProps,
  InstantEverythingProps,
  ProductsProps,
  TrustProps,
} from '@/components/shared';
import type { BasePageConfig } from '@/pageConfig/BasePageConfig';
import { sharedConfig } from '@/pageConfig/shared';
import aRated from '@/static/assets/home/a-rated.png';
import bCorp from '@/static/assets/home/b-corp.png';
import room from '@/static/assets/home/living-room-with-checkmarks.svg';
import nyse from '@/static/assets/home/nyse.png';
import pizzaDesktop from '@/static/assets/home/pizza_desktop_en-NL.svg';
import pizzaMobile1 from '@/static/assets/home/pizza_mobile_1.svg';
import pizzaMobile2 from '@/static/assets/home/pizza_mobile_2.svg';
import pizzaMobile3 from '@/static/assets/home/pizza_mobile_3.svg';

const pizza = {
  desktop: pizzaDesktop,
  mobile: {
    1: pizzaMobile1,
    2: pizzaMobile2,
    3: pizzaMobile3,
  },
};

export interface Config extends BasePageConfig {
  hero: HeroProps;
  customersReviews: CustomersReviewsProps;
  products: ProductsProps;
  genericProducts: GenericProductsProps;
  instantEverything: InstantEverythingProps;
  howItWorks: HowItWorksProps;
  trust: TrustProps;
}

export const config: Config = {
  seo: { ...sharedConfig['en-NL'].seo },
  layout: {
    ...sharedConfig['en-NL'].layout,
    showAskUsAnything: true,
    alignLogoOnMobile: 'left',
    headerActions: {
      ...sharedConfig['en-NL'].layout.headerActions,
      CTAs: {
        ...sharedConfig['en-NL'].layout.headerActions.CTAs,
        checkOurPrices: {
          ...sharedConfig['en-NL'].layout.headerActions.CTAs.checkOurPrices,
          short: {
            singular: 'Check our price',
            plural: 'Check our prices',
          },
        },
      },
    },
  },
  hero: {
    title: 'Forget everything you\nknow about insurance',
    subtitle: 'Instant everything. Great prices. Big heart.',
    ctaLabel: 'Check our Prices',
    cart: {
      singular: 'Back to quote',
      plural: 'Back to quotes',
    },
    enableRef: false,
  },
  products: {
    title: 'Incredible prices. No paperwork. Cancel any time.',
    trackingLabel: 'home',
    layout: {
      mt: [-10, -50],
    },
    cardsWidth: {
      desktop: 300,
    },
    products: [
      {
        title: 'Contents',
        subtitle: 'Cover for your stuff, both inside and outside of your home',
        price: 'From €5 a month',
        product: 'home_uk_contents',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
        alt: 'Contents Insurance',
        absoluteUrl: `${process.env.NEXT_PUBLIC_UNICLIENT_URL}/nl/home/quote?f=1&productCoverage=contents_and_liability`,
      },
      {
        title: 'Buildings',
        subtitle: 'Protection for homeowners in case of structural damage or accidents',
        price: 'From €17 a month',
        product: 'home_uk_buildings',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
        alt: 'Homeowners Insurance',
        absoluteUrl: `${process.env.NEXT_PUBLIC_UNICLIENT_URL}/nl/home/quote?f=1&productCoverage=buildings_and_liability`,
      },
      {
        title: 'Buildings & Contents',
        subtitle: 'Protects you, your home\nand your stuff',
        price: 'From €21 a month',
        product: 'home_uk_buildings_contents',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
        alt: 'Landlords Insurance',
        absoluteUrl: `${process.env.NEXT_PUBLIC_UNICLIENT_URL}/nl/home/quote?f=1&productCoverage=buildings_contents_and_liability`,
      },
    ],
    enableRef: true,
    darkBackground: true,
  },
  customersReviews: {
    darkBackground: true,
    title: 'The (Almost) 5 Star Insurance Company',
    subtitle: 'Lemonade has earned 4.9 stars in the App store',
    tweetsBackBandColor: '#f7f7f7',
  },
  genericProducts: {
    title: 'Home and Liability Insurance\nBuilt For the 21st Century',
    subtitle: 'Award winning protection for you, your stuff, and your home',
    bgImage: room.src,
  },
  instantEverything: {
    title: 'Instant Everything',
    subtitle:
      'Maya, our charming artificial intelligence bot, will craft the perfect coverage for you.\nIt couldn’t be easier, or faster',
    appSketchContentLeft: 'Get insured\n<b>In Seconds</b>',
    appSketchContentRight: 'Get paid\n<b>In Minutes</b>',
    darkBackground: true,
    adjustments: {
      contentSectionLeft: {
        left: { lg: '-435px' },
      },
      contentSectionRight: {
        right: { lg: '-415px' },
      },
    },
    layout: {
      mt: [-30, -50],
    },
  },

  howItWorks: {
    darkBackground: false,
    title: 'How Lemonade Works',
    showBrandsImage: false,
    subtitle: `Lemonade reverses the traditional insurance model. We treat the premiums you pay as if it's your money, not ours. With Lemonade, everything becomes simple and transparent. We take a flat fee, pay claims super fast,\nand <a aria-labelledby="how-it-works-subtitle" href="/nl/en/giveback">give back</a> what’s left to causes you care about.*`,
    disclaimer: `* Subject to board discretion and the company meeting certain financial standards. <a href="/nl/en/faq#giveback">Read more here</a>`,
    pizzaDesktop: pizza.desktop.src,
    pizzaMobile: [
      {
        image: pizza.mobile[1].src,
        text: 'A <b>flat fee</b> to </br> run everything',
      },
      {
        image: pizza.mobile[2].src,
        text: 'We pay claims <br/> <b>super fast</b>',
      },
      {
        image: pizza.mobile[3].src,
        text: `Good luck and good behavior let us <b>give back to causes</b>`,
      },
    ],
  },
  trust: {
    bulletsLayout: 'horizontal',
    bullets: [
      {
        name: 'social',
        title: 'Designed for Social Impact',
        text: 'Lemonade Inc. is a Public Benefit Corporation and certified B-Corp. Social impact is part of our legal mission and business model - not just marketing fluff',

        image: {
          src: bCorp,
          width: 103,
          height: 88,
        },
      },
      {
        name: 'nyse',
        title: 'NYSE:LMND',
        text: 'Lemonade is a publicly traded company, listed on the New York Stock Exchange under the LMND stock symbol',
        image: {
          src: nyse,
          width: 100,
          height: 89,
        },
      },
      {
        name: 'rating',
        title: 'A-Rated and Backed by Giants',
        text: 'Lemonade is rated A-Exceptional, fully licensed, and reinsured by some of the most trusted names on the planet',
        image: {
          src: aRated,
          width: 100,
          height: 89,
        },
      },
    ],
  },
};
